import React, { useState } from "react";
import { FaEdit, FaTrash, FaCheck, FaTimes, FaPowerOff, FaServer, FaEye, FaCube } from "react-icons/fa";
import { DesignSystem } from "../../styles/designSystem";
import { Rack3D } from "./Rack3D";

interface RackUnitProps {
  rackUnit?: RackUnitInterface;
  position: number;
  updateUnit: (unitData: RackUnitInterface, changes: Partial<RackUnitInterface>) => Promise<void>;
  deleteUnit?: (unitData: RackUnitInterface) => Promise<void>;
  isOccupiedByAbove?: boolean;
  onDragStart?: (unit: RackUnitInterface, position: number) => void;
  onDragOver?: (position: number) => void;
  onDrop?: (position: number) => void;
  forceViewMode?: 'front' | 'side' | 'rear' | '3d';
  onAddAsset?: (position: number) => void;
  hasAsset?: boolean;
  assetName?: string;
  onViewAsset?: (unitId: string) => void;
  projectId?: string;
}

export const RackUnit: React.FC<RackUnitProps> = ({
  rackUnit,
  position,
  updateUnit,
  deleteUnit,
  isOccupiedByAbove = false,
  onDragStart,
  onDragOver,
  onDrop,
  forceViewMode,
  onAddAsset,
  hasAsset = false,
  assetName,
  onViewAsset,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deviceModel, setDeviceModel] = useState(rackUnit?.device_model || "");
  const [reservedUnits, setReservedUnits] = useState(rackUnit?.reserved_units || 1);
  const [powerUsage, setPowerUsage] = useState(rackUnit?.power_usage || 0);
  const currentViewMode = forceViewMode || 'front';

  // If position is occupied by device above - DO NOT RENDER ANYTHING
  if (isOccupiedByAbove) {
    return null;
  }

  const unitCount = rackUnit?.reserved_units || 1;
  const isMultiUnit = unitCount > 1;
  
  // Height of one unit (data center standard - 44mm ≈ 50px)
  const UNIT_HEIGHT = 50;
  const totalHeight = unitCount * UNIT_HEIGHT;

  // Functions (logic remains the same)
  const handleSave = async () => {
    if (!rackUnit) return;
    setIsLoading(true);
    try {
      await updateUnit(rackUnit, {
        device_model: deviceModel,
        reserved_units: reservedUnits,
        power_usage: powerUsage,
      });
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating unit:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // handleCreate removed - now only assets can be added to empty positions

  const handleDelete = async () => {
    if (!rackUnit || !deleteUnit) return;
    setIsLoading(true);
    try {
      await deleteUnit(rackUnit);
    } catch (error) {
      console.error("Error deleting unit:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setDeviceModel(rackUnit?.device_model || "");
    setReservedUnits(rackUnit?.reserved_units || 1);
    setPowerUsage(rackUnit?.power_usage || 0);
  };

  const handleDragStart = () => {
    if (rackUnit && onDragStart) {
      onDragStart(rackUnit, position);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    onDragOver?.(position);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    onDrop?.(position);
  };

  // REAR VIEW - Rear panel without cables
  const renderRearView = () => {
    return (
      <div
        style={{ height: `${totalHeight}px` }}
        className="relative group mb-px"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div
          className={`
            h-full bg-[#171717] border-l-4 border-r border-t border-b border-[#444]
            cursor-move transition-all duration-200 relative overflow-hidden
            ${isMultiUnit ? 'border-l-amber-500' : 'border-l-green-500'}
            hover:bg-[#333] hover:border-[#555]
          `}
          draggable={!isEditing}
          onDragStart={handleDragStart}
        >
          {/* Rear panel */}
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900">
            {/* Denser ventilation holes */}
            <div className="absolute inset-0 opacity-30">
              <div className="grid grid-cols-16 gap-1 h-full p-1">
                {Array.from({ length: 80 }, (_, i) => (
                  <div key={i} className="bg-black rounded-full h-1" />
                ))}
              </div>
            </div>
          </div>

          <div className="relative z-10 h-full flex items-center">
            {/* Left panel with positions */}
            <div className="w-14 bg-black/30 h-full flex flex-col justify-center items-center border-r border-[#555]">
              {isMultiUnit ? (
                <div className="flex flex-col items-center justify-between h-full py-1 text-center">
                  {Array.from({ length: unitCount }, (_, i) => (
                    <div key={i} className="flex flex-col items-center justify-center flex-1">
                      <span className="text-green-400 font-mono text-xs font-bold">
                        {String(position + unitCount - 1 - i).padStart(2, '0')}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-green-400 font-mono text-sm font-bold">
                  {String(position).padStart(2, '0')}
                </span>
              )}
            </div>

            {/* Central area - clean rear panel */}
            <div className="flex-1 px-4 py-2 h-full">
              <div className="bg-black/20 rounded border border-[#555] h-full flex items-center justify-center">
                <div className="text-center">
                  <div className="text-white font-mono text-sm font-semibold mb-1">
                    {rackUnit?.device_model || "UNNAMED_DEVICE"}
                  </div>
                  <div className="text-[#aaa] font-mono text-xs">
                    REAR PANEL
                  </div>
                  <div className="text-[#888] font-mono text-xs mt-2">
                    NO CABLES VIEW
                  </div>
                </div>
              </div>
            </div>

            {/* Right panel with view switcher */}
            <div className="w-16 bg-black/30 h-full flex flex-col justify-center items-center border-l border-[#555]">
              
              {isEditing ? (
                <div className="flex flex-col space-y-1">
                  <button
                    onClick={handleSave}
                    disabled={isLoading}
                    className="p-1.5 bg-[#171717] text-white rounded text-xs hover:bg-green-500 disabled:opacity-50"
                  >
                    <FaCheck size={10} />
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={isLoading}
                    className="p-1.5 bg-[#171717] text-white rounded text-xs hover:bg-[#666] disabled:opacity-50"
                  >
                    <FaTimes size={10} />
                  </button>
                </div>
              ) : (
                <div className="flex flex-col space-y-1 items-center">
                  <button
                    onClick={() => setIsEditing(true)}
                    className="p-1 bg-[#171717] text-white rounded text-xs hover:bg-[#333] opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <FaEdit size={8} />
                  </button>
                  {deleteUnit && (
                    <button
                      onClick={handleDelete}
                      disabled={isLoading}
                      className="p-1 bg-red-600 text-white rounded text-xs hover:bg-red-500 disabled:opacity-50 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <FaTrash size={8} />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // SIDE VIEW - Rear panel with cables
  const renderSideView = () => {
    return (
      <div
        style={{ height: `${totalHeight}px` }}
        className="relative group mb-px"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div
          className={`
            h-full bg-[#171717] border-l-4 border-r border-t border-b border-[#444]
            cursor-move transition-all duration-200 relative overflow-hidden
            ${isMultiUnit ? 'border-l-amber-500' : 'border-l-green-500'}
            hover:bg-[#333] hover:border-[#555]
          `}
          draggable={!isEditing}
          onDragStart={handleDragStart}
        >
          {/* Rear panel */}
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900">
            {/* Ventilation holes */}
            <div className="absolute inset-0 opacity-20">
              <div className="grid grid-cols-12 gap-1 h-full p-2">
                {Array.from({ length: 48 }, (_, i) => (
                  <div key={i} className="bg-black rounded h-1" />
                ))}
              </div>
            </div>
          </div>

          <div className="relative z-10 h-full flex items-center">
            {/* Left panel with positions */}
            <div className="w-14 bg-black/30 h-full flex flex-col justify-center items-center border-r border-[#555]">
              {isMultiUnit ? (
                <div className="flex flex-col items-center justify-between h-full py-1 text-center">
                  {Array.from({ length: unitCount }, (_, i) => (
                    <div key={i} className="flex flex-col items-center justify-center flex-1">
                      <span className="text-green-400 font-mono text-xs font-bold">
                        {String(position + unitCount - 1 - i).padStart(2, '0')}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-green-400 font-mono text-sm font-bold">
                  {String(position).padStart(2, '0')}
                </span>
              )}
            </div>

            {/* Central area - rear panel with ports */}
            <div className="flex-1 px-2 py-1 h-full">
              <div className="bg-black/50 rounded border border-[#444] h-full flex items-center justify-between px-2">
                {/* Ports and connectors */}
                <div className="flex flex-col space-y-1">
                  {/* Network ports */}
                  <div className="flex space-x-1">
                    {Array.from({ length: 4 }, (_, i) => (
                      <div key={i} className="w-4 h-2 bg-[#171717] rounded-sm border border-blue-400" />
                    ))}
                  </div>
                  {/* Additional ports for multi-unit devices */}
                  {isMultiUnit && (
                    <div className="flex space-x-1">
                      {Array.from({ length: 2 }, (_, i) => (
                        <div key={i} className="w-4 h-2 bg-yellow-600 rounded-sm border border-yellow-400" />
                      ))}
                    </div>
                  )}
                </div>

                {/* Central information */}
                <div className="flex-1 text-center">
                  <div className="text-white font-mono text-xs font-semibold">
                    {rackUnit?.device_model || "UNNAMED_DEVICE"}
                  </div>
                  <div className="text-[#aaa] font-mono text-xs">
                    REAR PANEL
                  </div>
                </div>

                {/* Power connectors */}
                <div className="flex flex-col space-y-1">
                  <div className="w-3 h-3 bg-red-600 rounded border border-red-400" />
                  {powerUsage > 500 && (
                    <div className="w-3 h-3 bg-red-600 rounded border border-red-400" />
                  )}
                </div>
              </div>
            </div>

            {/* Right panel with view switcher */}
            <div className="w-16 bg-black/30 h-full flex flex-col justify-center items-center border-l border-[#555]">
              
              {isEditing ? (
                <div className="flex flex-col space-y-1">
                  <button
                    onClick={handleSave}
                    disabled={isLoading}
                    className="p-1.5 bg-[#171717] text-white rounded text-xs hover:bg-green-500 disabled:opacity-50"
                  >
                    <FaCheck size={10} />
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={isLoading}
                    className="p-1.5 bg-[#171717] text-white rounded text-xs hover:bg-[#666] disabled:opacity-50"
                  >
                    <FaTimes size={10} />
                  </button>
                </div>
              ) : (
                <div className="flex flex-col space-y-1 items-center">
                  <button
                    onClick={() => setIsEditing(true)}
                    className="p-1 bg-[#171717] text-white rounded text-xs hover:bg-[#333] opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <FaEdit size={8} />
                  </button>
                  {deleteUnit && (
                    <button
                      onClick={handleDelete}
                      disabled={isLoading}
                      className="p-1 bg-red-600 text-white rounded text-xs hover:bg-red-500 disabled:opacity-50 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <FaTrash size={8} />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Power cables */}
          <div className="absolute right-0 top-0 h-full w-2 pointer-events-none">
            {/* Main power cable */}
            <div 
              className="absolute right-0 w-1 bg-red-600 rounded-full shadow-[0_4px_12px_rgba(0,0,0,0.08)]"
              style={{ 
                height: `${totalHeight * 0.8}px`, 
                top: `${totalHeight * 0.1}px` 
              }}
            />
            {/* Additional cable for high-power devices */}
            {powerUsage > 500 && (
              <div 
                className="absolute right-0 w-1 bg-red-700 rounded-full shadow-[0_4px_12px_rgba(0,0,0,0.08)] ml-0.5"
                style={{ 
                  height: `${totalHeight * 0.6}px`, 
                  top: `${totalHeight * 0.2}px` 
                }}
              />
            )}
          </div>

          {/* Network cables */}
          <div className="absolute right-0 top-0 h-full w-1 pointer-events-none">
            {Array.from({ length: Math.min(3, unitCount) }, (_, i) => (
              <div 
                key={i}
                className="absolute right-0 w-px bg-[#555] rounded-full shadow-[0_2px_8px_rgba(0,0,0,0.06)]"
                style={{ 
                  height: `${totalHeight * 0.3}px`, 
                  top: `${(totalHeight * 0.2) + (i * totalHeight * 0.15)}px` 
                }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

  // FRONT VIEW - Original design
  const renderFrontView = () => {
    return (
      <div
        style={{ height: `${totalHeight}px` }}
        className="relative group mb-px"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div
          className={`
            h-full bg-[#171717] border-l-4 border-r border-t border-b border-[#444]
            cursor-move transition-all duration-200 relative overflow-hidden
            ${isMultiUnit ? 'border-l-amber-500' : 'border-l-green-500'}
            hover:bg-[#333] hover:border-[#555]
          `}
          draggable={!isEditing}
          onDragStart={handleDragStart}
        >
          {/* Front panel with holes */}
          <div className="absolute inset-0 opacity-10">
            <div className="grid grid-cols-20 gap-1 h-full p-2">
              {Array.from({ length: 60 }, (_, i) => (
                <div key={i} className="bg-[#bbb] rounded-full opacity-30" />
              ))}
            </div>
          </div>

          <div className="relative z-10 h-full flex items-center">
            {/* Left panel with positions */}
            <div className="w-14 bg-black/30 h-full flex flex-col justify-center items-center border-r border-[#555]">
              {isMultiUnit ? (
                <div className="flex flex-col items-center justify-between h-full py-1 text-center">
                  {Array.from({ length: unitCount }, (_, i) => (
                    <div key={i} className="flex flex-col items-center justify-center flex-1">
                      <span className="text-green-400 font-mono text-xs font-bold">
                        {String(position + unitCount - 1 - i).padStart(2, '0')}
                      </span>
                      {i === Math.floor(unitCount / 2) && (
                        <div className="text-xs text-amber-400 font-mono mt-1 bg-amber-900/50 px-1 rounded">
                          {unitCount}U
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-green-400 font-mono text-sm font-bold">
                  {String(position).padStart(2, '0')}
                </span>
              )}
            </div>

            {/* Central area with device information */}
            <div className="flex-1 px-4 py-2">
              {isEditing ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={deviceModel}
                    onChange={(e) => setDeviceModel(e.target.value)}
                    className="w-full bg-[#333] text-white border border-[#555] rounded px-3 py-1 text-sm focus:outline-none focus:border-green-500 font-mono"
                    placeholder="Device Model"
                    disabled={isLoading}
                  />
                  <div className="flex space-x-4">
                    <select
                      value={reservedUnits}
                      onChange={(e) => setReservedUnits(parseInt(e.target.value))}
                      className="bg-[#333] text-white border border-[#555] rounded px-2 py-1 text-xs focus:outline-none focus:border-green-500 font-mono"
                      disabled={isLoading}
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                        <option key={num} value={num}>{num}U</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      value={powerUsage}
                      onChange={(e) => setPowerUsage(parseInt(e.target.value) || 0)}
                      className="w-16 bg-[#333] text-white border border-[#555] rounded px-2 py-1 text-xs focus:outline-none focus:border-green-500 font-mono"
                      placeholder="W"
                      min="0"
                      disabled={isLoading}
                    />
                  </div>
                </div>
              ) : (
                <div onClick={() => setIsEditing(true)} className="cursor-pointer">
                  <div className="flex items-center space-x-3 mb-1">
                    <FaServer className="text-green-400" size={16} />
                    <span className="text-white font-mono text-sm font-semibold">
                      {rackUnit?.device_model || "UNNAMED_DEVICE"}
                    </span>
                  </div>
                  <div className="flex items-center space-x-4 text-xs font-mono">
                    <span className="text-[#aaa]">
                      {isMultiUnit ? `${unitCount}U RACK` : "1U RACK"}
                    </span>
                    {isMultiUnit && (
                      <span className="text-amber-400">
                        POS:{position}-{position + unitCount - 1}
                      </span>
                    )}
                    <span className="text-[#aaa] flex items-center space-x-1">
                      <FaPowerOff size={10} />
                      <span>{powerUsage}W</span>
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Right panel - indicators and buttons */}
            <div className="w-16 bg-black/30 h-full flex flex-col justify-center items-center border-l border-[#555]">
              
              {isEditing ? (
                <div className="flex flex-col space-y-1">
                  <button
                    onClick={handleSave}
                    disabled={isLoading}
                    className="p-1.5 bg-[#171717] text-white rounded text-xs hover:bg-green-500 disabled:opacity-50"
                  >
                    <FaCheck size={10} />
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={isLoading}
                    className="p-1.5 bg-[#171717] text-white rounded text-xs hover:bg-[#666] disabled:opacity-50"
                  >
                    <FaTimes size={10} />
                  </button>
                </div>
              ) : (
                <div className="flex flex-col space-y-2 items-center">
                  {/* LED indicators */}
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" title="Power" />
                  <div className="w-2 h-2 bg-[#555] rounded-full" title="Network" />
                  
                  {/* Action buttons */}
                  <div className="flex flex-col space-y-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => setIsEditing(true)}
                      className="p-1 bg-[#171717] text-white rounded text-xs hover:bg-[#333]"
                    >
                      <FaEdit size={8} />
                    </button>
                    {deleteUnit && (
                      <button
                        onClick={handleDelete}
                        disabled={isLoading}
                        className="p-1 bg-red-600 text-white rounded text-xs hover:bg-red-500 disabled:opacity-50"
                      >
                        <FaTrash size={8} />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // NEW DESIGN: Server device
  if (rackUnit) {
    switch (currentViewMode) {
      case 'side':
        return renderSideView();
      case 'rear':
        return renderRearView();
      default:
        return renderFrontView();
    }
  }

  // Creation mode removed - now only adding assets from list

  // Empty slot
  return (
    <div
      style={{ height: `${UNIT_HEIGHT}px` }}
      className="relative group mb-px"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {hasAsset ? (
        // Show asset information
        <div className="w-full h-full bg-blue-900/80 border border-[#171717] transition-colors flex items-center group">
          <div className="w-14 bg-blue-800/50 h-full flex items-center justify-center border-r border-[#171717]">
            <span className="text-blue-300 font-mono text-sm">
              {String(position).padStart(2, '0')}
            </span>
          </div>
          
          <div className="flex-1 flex items-center justify-between px-3">
            <div className="flex items-center space-x-2">
              <FaServer className="text-blue-300" size={12} />
              <span className="text-blue-100 font-mono text-xs font-semibold">
                {assetName || 'Asset Assigned'}
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              {rackUnit && (rackUnit as any).id && onViewAsset && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewAsset((rackUnit as any).id);
                  }}
                  className="flex items-center gap-1 px-2 py-1 text-xs bg-[#171717] hover:bg-[#333] text-white rounded transition-colors"
                  title="View Asset Details"
                >
                  <FaEye className="text-xs" />
                  View
                </button>
              )}
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
              <span className="text-blue-300 font-mono text-xs">ASSET</span>
            </div>
          </div>
        </div>
      ) : (
        // Empty slot with buttons
        <div className="w-full h-full bg-[#171717]/50 border border-[#444] border-dashed hover:bg-[#333]/50 hover:border-[#555] transition-colors flex items-center group">
          <div className="w-14 bg-black/20 h-full flex items-center justify-center border-r border-[#444]">
            <span className="text-[#888] font-mono text-sm">
              {String(position).padStart(2, '0')}
            </span>
          </div>
          
          <div className="flex-1 flex items-center justify-center text-[#888] group-hover:text-[#aaa]">
            {onAddAsset ? (
              <button
                onClick={() => onAddAsset(position)}
                className="flex items-center space-x-2 hover:text-blue-400 transition-colors px-3 py-1 rounded hover:bg-blue-900/20"
                title="Add Asset from Inventory"
              >
                <FaServer size={14} />
                <span className="font-mono text-sm uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
                  ADD ASSET
                </span>
              </button>
            ) : (
              <span className="font-mono text-xs text-[#555]">
                NO ASSET SUPPORT
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Mini rack for carousel
export const MiniRack: React.FC<{
  rack: RackInterface;
  isBlurred?: boolean;
  onClick?: () => void;
}> = ({ rack, isBlurred = false, onClick }) => {
  return (
    <div
      className={`cursor-pointer transition-all duration-300 transform hover:scale-105 ${
        isBlurred ? "blur-sm opacity-60 hover:opacity-80 hover:blur-none" : ""
      }`}
      onClick={onClick}
    >
      <div className="bg-[#171717] rounded border border-[#444] p-3 w-48 h-96 shadow-[0_8px_30px_rgba(0,0,0,0.12)]">
        <div className="text-center mb-2 border-b border-[#444] pb-2">
          <div className="text-green-400 font-mono font-bold">{rack.number}</div>
          <div className="text-xs text-[#888] font-mono">
            {rack.units?.length || 0}/{rack.total_units || 45} UNITS
          </div>
          {/* Units statistics */}
          <div className="flex items-center justify-center space-x-3 text-xs mt-2">
            <div className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
              <span className="text-blue-300">{rack.total_units || 45}</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
              <span className="text-green-300">{Math.max(0, (rack.total_units || 45) - (rack.units?.length || 0))}</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-amber-400 rounded-full"></div>
              <span className="text-amber-300">{rack.units?.length || 0}</span>
            </div>
          </div>
        </div>
        
        <div className="bg-black rounded border border-[#333] p-2 h-80">
          <div className="space-y-px">
            {new Array(Math.min(rack.total_units || 45, 20)).fill(null).map((_, index) => {
              const position = (rack.total_units || 45) - index;
              const rackUnit = rack.units?.find((u) => u.position === position);
              
              return (
                <div
                  key={position}
                  className={`h-3 rounded-sm ${
                    rackUnit 
                      ? "bg-green-500 border-l-2 border-amber-400" 
                      : "bg-[#333] border border-[#444]"
                  }`}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Component for showing rack from three sides like in real data center
interface MultiViewRackProps {
  rack: RackInterface;
  updateUnitHandler: (unitData: RackUnitInterface, changes: Partial<RackUnitInterface>) => Promise<void>;
  deleteUnitHandler: (unitData: RackUnitInterface) => Promise<void>;
  getOccupiedPositions: (excludeUnitId?: string) => { occupied: Set<number> };
  onDragStart: (unit: RackUnitInterface, position: number) => void;
  onDragOver: (position: number) => void;
  onDrop: (position: number) => void;
  isCleanView?: boolean;
  onAddAsset?: (position: number) => void;
  hasAsset?: (position: number) => boolean;
  assetName?: (position: number) => string | undefined;
  onViewAsset?: (unitId: string) => void;
  projectId?: string;
}

export const MultiViewRack: React.FC<MultiViewRackProps> = ({
  rack,
  updateUnitHandler,
  deleteUnitHandler,
  getOccupiedPositions,
  onDragStart,
  onDragOver,
  onDrop,
  isCleanView = false,
  onAddAsset,
  hasAsset,
  assetName,
  onViewAsset,
  projectId,
}) => {
  const perspectives = [
    { 
      name: 'Side View', 
      mode: 'side' as const, 
      gradient: 'from-gray-600 to-gray-800', 
      description: 'Side view with cables and connections'
    },
    { 
      name: 'Front View', 
      mode: 'front' as const, 
      gradient: 'from-gray-600 to-gray-800', 
      description: 'Main front panel interface'
    },
    { 
      name: 'Rear View', 
      mode: 'rear' as const, 
      gradient: 'from-gray-600 to-gray-800', 
      description: 'Back panel connections'
    },
    { 
      name: '3D View', 
      mode: '3d' as const, 
      gradient: 'from-purple-600 to-purple-800', 
      description: 'Interactive 3D visualization'
    },
  ];

  if (isCleanView) {
    return (
      <div>
        {/* Three racks in clean view */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {perspectives.map((perspective) => (
            <div 
              key={perspective.mode} 
              className="bg-white rounded-lg shadow-[0_1px_2px_rgba(0,0,0,0.04)] border border-[#eaeaea] transition-all duration-300 hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)]"
            >
              {/* Clean header */}
              <div className="bg-[#fafafa] rounded-t-lg p-4 border-b border-[#eaeaea]">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-lg" style={{ color: DesignSystem.colors.gray[900] }}>
                      {perspective.name}
                    </h3>
                    <p className="text-xs" style={{ color: DesignSystem.colors.gray[600] }}>
                      {perspective.description}
                    </p>
                  </div>
                  <div className="bg-white px-3 py-1 rounded-full text-sm font-mono border border-[#eaeaea]">
                    RACK-{rack?.number || 'XX'}
                  </div>
                </div>
              </div>

              {/* Clean content */}
              <div className="p-4">
                <div className="space-y-1">
                  {new Array(rack?.total_units || 45).fill(null).map((_, index) => {
                    const position = (rack?.total_units || 45) - index;
                    const rackUnit = rack?.units?.find((u) => u.position === position);
                    const { occupied } = getOccupiedPositions();
                    const isOccupiedByAbove = occupied.has(position);

                    if (isOccupiedByAbove) {
                      return null;
                    }

                    return (
                      <div
                        key={`${perspective.mode}-${position}`}
                        className={`flex items-center justify-between p-2 rounded border ${
                          rackUnit 
                            ? 'bg-[#fafafa] border-[#eaeaea]' 
                            : 'bg-white border-[#eaeaea] border-dashed'
                        }`}
                        style={{ minHeight: '32px' }}
                      >
                        <div className="flex items-center space-x-3">
                          <div 
                            className="text-xs font-mono w-8 text-center"
                            style={{ color: DesignSystem.colors.gray[500] }}
                          >
                            {String(position).padStart(2, '0')}
                          </div>
                          <div className="text-sm">
                            {rackUnit ? (
                              <span style={{ color: DesignSystem.colors.gray[900] }}>
                                {rackUnit.device_model || 'Unknown Device'}
                              </span>
                            ) : (
                              <span style={{ color: DesignSystem.colors.gray[500] }}>
                                Empty slot
                              </span>
                            )}
                          </div>
                        </div>
                        {rackUnit && (
                          <div className="text-xs" style={{ color: DesignSystem.colors.gray[600] }}>
                            {rackUnit.power_usage}W
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Clean footer */}
              <div className="bg-[#fafafa] rounded-b-lg p-3 border-t border-[#eaeaea]">
                <div className="flex items-center justify-center space-x-4 text-xs" style={{ color: DesignSystem.colors.gray[600] }}>
                  <span>Height: {rack?.total_units || 45}U</span>
                  <span>•</span>
                  <span>Devices: {rack?.units?.length || 0}</span>
                  <span>•</span>
                  <span>Power: {rack?.max_power || 0}W</span>
                </div>
                {/* Units statistics */}
                <div className="flex items-center justify-center space-x-6 text-xs mt-2 pt-2 border-t border-[#eaeaea]">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                    <span style={{ color: DesignSystem.colors.gray[700] }}>{rack?.total_units || 45} Units</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span style={{ color: DesignSystem.colors.gray[700] }}>{Math.max(0, (rack?.total_units || 45) - (rack?.units?.length || 0))} free</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                    <span style={{ color: DesignSystem.colors.gray[700] }}>{rack?.units?.length || 0} occupied</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Three racks without rotations */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
        {perspectives.map((perspective) => (
          <div 
            key={perspective.mode} 
            className="transform transition-all duration-300 hover:scale-105 shadow-[0_8px_30px_rgba(0,0,0,0.12)]"
          >
            {/* Perspective header */}
            <div className={`bg-gradient-to-r ${perspective.gradient} rounded-t-xl p-4 text-white`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {perspective.mode === '3d' ? (
                    <FaCube className="w-5 h-5" />
                  ) : perspective.mode === 'side' ? (
                    <FaServer className="w-5 h-5" />
                  ) : perspective.mode === 'rear' ? (
                    <FaServer className="w-5 h-5" />
                  ) : (
                    <FaServer className="w-5 h-5" />
                  )}
                <div>
                  <h3 className="font-bold text-lg">{perspective.name}</h3>
                  <p className="text-xs opacity-90">{perspective.description}</p>
                  </div>
                </div>
                <div className="bg-white/20 px-3 py-1 rounded-full text-sm font-mono">
                  RACK-{rack?.number || 'XX'}
                </div>
              </div>
            </div>

            {/* Rack frame */}
            <div className="bg-[#171717] border-x-4 border-b-4 border-[#444] rounded-b-xl overflow-hidden">
                              {/* Rack control panel */}
              <div className="bg-black p-3 border-b border-[#555]">
                <div className="flex items-center justify-between">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  </div>
                  <div className="text-green-400 font-mono text-xs">
                    {perspective.mode.toUpperCase()} VIEW
                  </div>
                </div>
              </div>

              {/* Rack interior */}
              {perspective.mode === '3d' ? (
                <div className="bg-[#333] p-2 h-96">
                  <Rack3D 
                    rack={rack} 
                    onUnitClick={(unit) => {
                      if (unit && onViewAsset) {
                        onViewAsset(unit.id || '');
                      }
                    }}
                  />
                </div>
              ) : (
              <div className="bg-[#333] p-2">
                <div className="space-y-px">
                  {new Array(rack?.total_units || 45).fill(null).map((_, index) => {
                    const position = (rack?.total_units || 45) - index;
                    const rackUnit = rack?.units?.find((u) => u.position === position);
                    const { occupied } = getOccupiedPositions();
                    const isOccupiedByAbove = occupied.has(position);

                    return (
                      <RackUnit
                        key={`${perspective.mode}-${position}`}
                        rackUnit={rackUnit}
                        position={position}
                        updateUnit={updateUnitHandler}
                        deleteUnit={deleteUnitHandler}
                        isOccupiedByAbove={isOccupiedByAbove}
                        onDragStart={onDragStart}
                        onDragOver={onDragOver}
                        onDrop={onDrop}
                        forceViewMode={perspective.mode}
                          onAddAsset={onAddAsset}
                          hasAsset={hasAsset?.(position)}
                          assetName={assetName?.(position)}
                          onViewAsset={onViewAsset}
                          projectId={projectId}
                      />
                    );
                  })}
                </div>
              </div>
              )}

              {/* Rack footer */}
              <div className={`bg-gradient-to-r ${perspective.gradient} p-2`}>
                <div className="flex items-center justify-center space-x-4 text-white text-xs">
                  <span>Height: {rack?.total_units || 45}U</span>
                  <span>•</span>
                  <span>Devices: {rack?.units?.length || 0}</span>
                  <span>•</span>
                  <span>Power: {rack?.max_power || 0}W</span>
                </div>
                {/* Units statistics */}
                <div className="flex items-center justify-center space-x-6 text-white text-xs mt-2 pt-2 border-t border-white/20">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                    <span>{rack?.total_units || 45} Units</span>
              </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span>{Math.max(0, (rack?.total_units || 45) - (rack?.units?.length || 0))} free</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                    <span>{rack?.units?.length || 0} occupied</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};